import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHero } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

export default function ApplyForm({ type, title, subtitle, image, blurb, messageLabel, extraFields = [], testId, successResolver }) {
  const heroImage = image ? (image.startsWith("http") ? image : `${process.env.REACT_APP_BACKEND_URL}${image}`) : "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [extra, setExtra] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const missingRequired = extraFields.some((f) => f.required && !extra[f.key]);
  const canSubmit = !!form.name && !!form.email && !missingRequired;
  const success = successResolver ? successResolver(extra) : { title: "Application Received", text: "A member of the League Committee will get back to you shortly." };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Please complete all required fields."); return; }
    setLoading(true);
    try {
      await api.post("/submissions", { type, ...form, extra });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={testId}>
      <PageHero title={title} subtitle={subtitle} image={heroImage} testId={`${testId}-hero`} />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {blurb && <p className="text-slate-300 leading-relaxed mb-10 text-base">{blurb}</p>}
        {sent ? (
          <div className="bg-navy-paper border border-gold/30 p-10 text-center" data-testid="form-success">
            <CheckCircle className="w-14 h-14 text-accent mx-auto mb-4" />
            <h3 className="font-display text-2xl font-bold text-white">{success.title}</h3>
            <p className="text-slate-400 mt-2">{success.text}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-navy-paper border border-slate-800 p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-slate-300">Full Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-none bg-navy border-slate-700" data-testid="form-name" />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-none bg-navy border-slate-700" data-testid="form-email" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-none bg-navy border-slate-700" data-testid="form-phone" />
            </div>
            {extraFields.map((f) => {
              if (f.showIf && extra[f.showIf.key] !== f.showIf.value) return null;
              return (
                <div key={f.key}>
                  {f.type !== "image" && <Label className="text-slate-300">{f.label}{f.required && <span className="text-gold ml-1">*</span>}</Label>}
                  {f.type === "select" ? (
                    <select
                      value={extra[f.key] || ""}
                      onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
                      className="mt-1.5 w-full h-10 px-3 rounded-none bg-navy border border-slate-700 text-white text-sm focus:outline-none focus:border-gold"
                      data-testid={`form-${f.key}`}
                    >
                      <option value="">Select…</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "radio" ? (
                    <div className="mt-2 space-y-2" data-testid={`form-${f.key}`}>
                      {f.options.map((o) => (
                        <label key={o} className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${extra[f.key] === o ? "border-gold bg-gold/5" : "border-slate-700 hover:border-slate-500"}`} data-testid={`form-${f.key}-${o.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                          <input type="radio" name={f.key} value={o} checked={extra[f.key] === o} onChange={() => setExtra({ ...extra, [f.key]: o })} className="accent-[#D4AF37] w-4 h-4" />
                          <span className="text-slate-200 text-sm">{o}</span>
                        </label>
                      ))}
                    </div>
                  ) : f.type === "image" ? (
                    <div className="mt-1.5">
                      <Label className="text-slate-300">{f.label}</Label>
                      <div className="mt-1.5">
                        <ImageUpload label="" value={extra[f.key] || ""} onChange={(url) => setExtra({ ...extra, [f.key]: url })} endpoint={f.endpoint || "/admin/upload"} testId={`form-${f.key}`} />
                      </div>
                      <p className="text-slate-500 text-[11px] mt-1.5">Your ID is stored securely and only used to verify your age (18+).</p>
                    </div>
                  ) : (
                    <Input
                      type={f.type || "text"}
                      value={extra[f.key] || ""}
                      onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
                      className="mt-1.5 rounded-none bg-navy border-slate-700"
                      data-testid={`form-${f.key}`}
                    />
                  )}
                </div>
              );
            })}
            <div>
              <Label className="text-slate-300">{messageLabel || "Message"}</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="mt-1.5 rounded-none bg-navy border-slate-700" data-testid="form-message" />
            </div>
            <Button type="submit" disabled={loading || !canSubmit} className="w-full bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest py-6 disabled:opacity-50" data-testid="form-submit">
              {loading ? "Sending…" : "Submit"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
